import {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {Alert, Checkbox, FormControlLabel} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import Page from "@/components/Page";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFModal from "@/components/Modal";
import VLFCard from "@/components/Card";
import VLFSelectNew from "@/components/Input/SelectNew";
import VLFTextField from "@/components/Input/TextField";
import ScoutingIndexEditor from "@/components/ScoutingIndexEditor";
import {getDsContext} from "@/utils/dsContext";
import {
	useScoutingIndices,
	saveScoutingIndex,
	removeScoutingIndex,
	initializeScoutingIndices,
} from "@/utils/scoutingStatisticsData";
import {INDEX_GROUPS, validateIndex} from "@/utils/scoutingIndices";
import "@/pages/Scouting/Relazioni/index.scss";
const option = (index) => ({
	value: index.id,
	label:
		index.label +
		" · " +
		(index.group === "goalkeeper" ? "portieri" : "movimento") +
		(!index.active ? " · inattivo" : ""),
});
const EMPTY_INDICES = [];
const clone = (value) => JSON.parse(JSON.stringify(value));
export default function ScoutingIndicesPage(props) {
	useSelector((s) => s.user);
	useSelector((s) => s.token);
	useSelector((s) => s.season?.id);
	return <IndicesPage key={getDsContext()} {...props} />;
}
function IndicesPage({handleNotification}) {
	const navigate = useNavigate();
	const source = useScoutingIndices();
	const indices = source.data?.indices || EMPTY_INDICES;
	const [draft, setDraft] = useState(null),
		[errors, setErrors] = useState([]),
		[confirm, setConfirm] = useState(null),
		[error, setError] = useState(""),
		[conflict, setConflict] = useState(false);
	useEffect(() => {
		if (!draft && source.status === "ready" && indices.length)
			setDraft(clone(indices[0]));
	}, [draft, indices, source.status]);
	const saved = indices.find((i) => i.id === draft?.id);
	const editableValue = (value) =>
		value
			? JSON.stringify({
					label: value.label,
					group: value.group,
					attitude: value.attitude || null,
					active: value.active,
					sorting: value.sorting,
					components: (value.components || []).map((c) => ({
						label: c.label,
						kind: c.kind,
						num: c.num,
						den: c.den || null,
						sub: c.sub || null,
						weight: c.weight,
						min: c.min,
						max: c.max,
					})),
				})
			: null;
	const dirty = editableValue(saved) !== editableValue(draft);
	const editable = source.canWrite && !conflict;
	const open = (value) => {
		setDraft(value ? clone(value) : null);
		setErrors([]);
		setError("");
		setConflict(false);
	};
	const report = (e) => {
		setError(
			e.response?.data?.error || e.message || "Operazione non riuscita.",
		);
		if (e.response?.status === 409) setConflict(true);
	};
	const reload = async () => {
		try {
			const data = await source.refresh(true);
			const current =
				data.indices.find((i) => i.id === draft?.id) || data.indices[0];
			open(current);
		} catch (e) {
			report(e);
		}
	};
	const save = async () => {
		const found = validateIndex(draft);
		setErrors(found);
		if (found.length) {
			setError(
				found.includes("weights")
					? "La somma dei pesi deve essere 100%"
					: "Completa i campi evidenziati",
			);
			return;
		}
		try {
			const result = await saveScoutingIndex(draft);
			open(result);
			handleNotification?.("Indice salvato sul server.", "success");
		} catch (e) {
			report(e);
		}
	};
	const confirmAction = async () => {
		try {
			if (confirm === "defaults") {
				await initializeScoutingIndices();
				setDraft(null);
			} else {
				await removeScoutingIndex(draft);
				setDraft(null);
			}
			setConfirm(null);
			setError("");
		} catch (e) {
			report(e);
			setConfirm(null);
		}
	};
	const actions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: () => navigate(-1),
			color: "info",
		},
		...(source.canWrite
			? [
					{
						icon: <AddIcon />,
						label: "Nuovo indice",
						disabled: source.busy || conflict,
						onClick: () =>
							open({
								label: "",
								group: INDEX_GROUPS[0].value,
								components: [],
								active: true,
								sorting: 100,
								version: 0,
							}),
					},
					...(draft
						? [
								{
									icon: <SaveIcon />,
									label: "Salva",
									title: "Salva indice",
									disabled: !editable || !dirty,
									onClick: save,
								},
							]
						: []),
					...(draft?.version
						? [
								{
									label: "Elimina indice",
									disabled: !editable,
									onClick: () => setConfirm("delete"),
									color: "error",
								},
							]
						: []),
					{
						label: "Indici iniziali mancanti",
						disabled: !editable,
						onClick: () => setConfirm("defaults"),
						color: "info",
					},
				]
			: []),
	];
	return (
		<Page className="page-container relazioni-page">
			<Breadcrumbs
				current="Indici statistiche"
				actions={<MainActions actions={actions} />}
			/>
			<div className="relazioni-stack">
				{(source.status === "idle" || source.status === "loading") && (
					<p>Caricamento indici condivisi…</p>
				)}
				{source.status === "error" && (
					<Alert severity="error">
						Indici non disponibili.{" "}
						<button onClick={() => source.refresh().catch(report)}>
							Riprova caricamento
						</button>
					</Alert>
				)}
				{error && <Alert severity="error">{error}</Alert>}
				{conflict && (
					<MainActions
						actions={[
							{
								label: "Ricarica dal server (scarta la bozza)",
								onClick: reload,
								disabled: source.busy,
							},
						]}
					/>
				)}
				{source.status === "ready" && !source.data?.canWrite && (
					<Alert severity="info">
						Consultazione: non hai il permesso di modificare gli indici.
					</Alert>
				)}
				{source.status === "ready" && !indices.length && !draft && (
					<Alert severity="info">Nessun indice configurato sul server.</Alert>
				)}
				{!!indices.length && (
					<VLFCard
						title="Indici"
						openable={false}
						content={
							<>
								<VLFSelectNew
									label="Indice da consultare o modificare"
									aria-label="Indice da consultare o modificare"
									options={indices.map(option)}
									value={saved ? option(saved) : null}
									placeholder={draft && !saved ? "Nuovo indice" : undefined}
									onChange={(o) =>
										o && open(indices.find((i) => i.id === o.value))
									}
									sorted={false}
									isClearable={false}
									isDisabled={source.busy}
								/>
								{dirty && (
									<p>
										Le modifiche non salvate vengono scartate cambiando indice.
									</p>
								)}
							</>
						}
					/>
				)}
				{draft && (
					<VLFCard
						title={saved?.label || "Nuovo indice"}
						openable={false}
						content={
							<>
								<fieldset
									disabled={!editable}
									style={{border: 0, padding: 0, minWidth: 0}}
								>
									<ScoutingIndexEditor
										index={draft}
										onChange={setDraft}
										errors={errors}
										disabled={!editable}
									/>
									<FormControlLabel
										control={
											<Checkbox
												checked={!!draft.active}
												disabled={!editable}
												onChange={(e) =>
													setDraft({...draft, active: e.target.checked})
												}
											/>
										}
										label="Indice attivo"
									/>
									<VLFTextField
										label="Ordine"
										inputProps={{"aria-label": "Ordine", min: 0, max: 10000}}
										type="number"
										value={draft.sorting}
										disabled={!editable}
										onChange={(e) =>
											setDraft({...draft, sorting: Number(e.target.value)})
										}
									/>
								</fieldset>
								<p className="relazione-hint">
									Componenti su scala 0–100, media pesata delle componenti
									calcolabili. Le configurazioni confermate sono condivise con
									gli altri utenti del club.
								</p>
							</>
						}
					/>
				)}
			</div>
			<VLFModal
				open={!!confirm}
				title={confirm === "defaults" ? "Indici iniziali" : "Eliminazione"}
				content={
					<p>
						{confirm === "defaults"
							? "Aggiungere soltanto gli indici iniziali mancanti? Le configurazioni già presenti saranno conservate."
							: `Eliminare l’indice «${draft?.label}»?`}
					</p>
				}
				confirm={confirmAction}
				close={() => !source.busy && setConfirm(null)}
				disabled={source.busy || !editable}
				confirmLabel="Conferma"
			/>
		</Page>
	);
}
